// Suporta tanto Vite (VITE_) quanto Next (NEXT_PUBLIC_) para facilitar configuração
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || import.meta.env.NEXT_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

// Em preview público, as variáveis podem não estar presentes no build.
// Em vez de lançar erro e quebrar a aplicação, vamos registrar um aviso.
if (!supabaseUrl || !supabaseAnonKey) {
  console.error(
    'Supabase: variáveis ausentes. Defina VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY em .env.local antes de build/preview.'
  );
}

// Usar cliente real do Supabase se variáveis estiverem disponíveis, caso contrário usar mock
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let supabase: any;

if (supabaseUrl && supabaseAnonKey) {
  // Variáveis disponíveis - usar cliente real
  try {
    const { createClient } = await import('@supabase/supabase-js');
    supabase = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true
      }
    });
  } catch (error) {
    console.error('Erro ao inicializar Supabase:', error);
    // Fallback para mock se houver erro na importação
    supabase = createMockSupabase();
  }
} else {
  // Variáveis não disponíveis - usar mock
  console.warn('Supabase: variáveis de ambiente não configuradas. Usando mock. Configure VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY em .env.local');
  supabase = createMockSupabase();
}

function createMockSupabase() {
  return {
    auth: {
      async getSession() {
        return { data: { session: null }, error: null };
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onAuthStateChange(_callback: (event: string, session: unknown) => void) {
        return {
          data: {
            subscription: {
              unsubscribe() {}
            }
          }
        };
      },
      async signInWithPassword() {
        return { 
          data: { user: null, session: null }, 
          error: new Error('Autenticação indisponível. Configure VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY em .env.local') 
        };
      },
      async signUp() {
        return { 
          data: { user: null, session: null }, 
          error: new Error('Cadastro indisponível. Configure VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY em .env.local') 
        };
      },
      async signOut() {
        return { error: null };
      },
      async verifyOtp() {
        return { 
          data: { user: null, session: null }, 
          error: new Error('Verificação OTP indisponível. Configure VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY em .env.local') 
        };
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    from(_table: string) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const chain: any = {
        select() { return chain; },
        insert() { return chain; },
        update() { return chain; },
        upsert() { return chain; },
        delete() { return chain; },
        eq() { return chain; },
        neq() { return chain; },
        gt() { return chain; },
        gte() { return chain; },
        lt() { return chain; },
        lte() { return chain; },
        like() { return chain; },
        ilike() { return chain; },
        is() { return chain; },
        in() { return chain; },
        contains() { return chain; },
        containedBy() { return chain; },
        rangeGt() { return chain; },
        rangeGte() { return chain; },
        rangeLt() { return chain; },
        rangeLte() { return chain; },
        rangeAdjacent() { return chain; },
        overlaps() { return chain; },
        textSearch() { return chain; },
        match() { return chain; },
        not() { return chain; },
        or() { return chain; },
        filter() { return chain; },
        order() { return chain; },
        limit() { return chain; },
        range() { return chain; },
        abortSignal() { return chain; },
        single() { return Promise.resolve({ data: null, error: null }); },
        maybeSingle() { return Promise.resolve({ data: null, error: null }); },
        csv() { return Promise.resolve({ data: '', error: null }); },
        geojson() { return Promise.resolve({ data: null, error: null }); },
        explain() { return Promise.resolve({ data: null, error: null }); },
        rollback() { return chain; },
        returns() { return chain; },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        then(onResolve: (value: any) => any, onReject?: (error: any) => any) {
          return Promise.resolve({ data: null, error: null }).then(onResolve, onReject);
        }
      };
      return chain;
    }
  };
}

export { supabase };

export type Database = {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string;
          full_name: string | null;
          age: number | null;
          avatar_url: string | null;
          created_at: string;
          xp: number;
          offensive_score: number;
          last_weekly_reset: string | null;
        };
        Insert: {
          id: string;
          full_name?: string | null;
          age?: number | null;
          avatar_url?: string | null;
          created_at?: string;
          xp?: number;
          offensive_score?: number;
          last_weekly_reset?: string | null;
        };
        Update: {
          id?: string;
          full_name?: string | null;
          age?: number | null;
          avatar_url?: string | null;
          created_at?: string;
          xp?: number;
          offensive_score?: number;
          last_weekly_reset?: string | null;
        };
      };
      user_goals: {
        Row: {
          id: string;
          user_id: string;
          goal_type: 'massa' | 'secar' | 'definir' | 'performance';
          training_days_week: number;
          experience_level: 'iniciante' | 'intermediario' | 'avancado';
          physical_restrictions: string | null;
          body_photos: string[] | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          goal_type: 'massa' | 'secar' | 'definir' | 'performance';
          training_days_week: number;
          experience_level: 'iniciante' | 'intermediario' | 'avancado';
          physical_restrictions?: string | null;
          body_photos?: string[] | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          goal_type?: 'massa' | 'secar' | 'definir' | 'performance';
          training_days_week?: number;
          experience_level?: 'iniciante' | 'intermediario' | 'avancado';
          physical_restrictions?: string | null;
          body_photos?: string[] | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      workout_templates: {
        Row: {
          id: string;
          name: string;
          type: 'old_school' | 'low_volume' | 'custom' | 'fisiculturistas';
          level: 'iniciante' | 'intermediario' | 'avancado' | null;
          description: string | null;
          user_id: string | null;
          created_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          type: 'old_school' | 'low_volume' | 'custom' | 'fisiculturistas';
          level?: 'iniciante' | 'intermediario' | 'avancado' | null;
          description?: string | null;
          user_id?: string | null;
          created_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          type?: 'old_school' | 'low_volume' | 'custom' | 'fisiculturistas';
          level?: 'iniciante' | 'intermediario' | 'avancado' | null;
          description?: string | null;
          user_id?: string | null;
          created_at?: string;
        };
      };
      workout_exercises: {
        Row: {
          id: string;
          workout_template_id: string;
          exercise_name: string;
          sets: number;
          reps: string;
          rest_seconds: number;
          video_url: string | null;
          tips: string | null;
          order: number;
          created_at: string;
        };
        Insert: {
          id?: string;
          workout_template_id: string;
          exercise_name: string;
          sets: number;
          reps: string;
          rest_seconds?: number;
          video_url?: string | null;
          tips?: string | null;
          order?: number;
          created_at?: string;
        };
        Update: {
          id?: string;
          workout_template_id?: string;
          exercise_name?: string;
          sets?: number;
          reps?: string;
          rest_seconds?: number;
          video_url?: string | null;
          tips?: string | null;
          order?: number;
          created_at?: string;
        };
      };
      user_workouts: {
        Row: {
          id: string;
          user_id: string;
          workout_template_id: string | null;
          scheduled_days: string[] | null;
          is_active: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          workout_template_id?: string | null;
          scheduled_days?: string[] | null;
          is_active?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          workout_template_id?: string | null;
          scheduled_days?: string[] | null;
          is_active?: boolean;
          created_at?: string;
          updated_at?: string;
        };
      };
      daily_progress: {
        Row: {
          id: string;
          user_id: string;
          date: string;
          water_ml: number;
          workout_completed: boolean;
          sleep_hours: number | null;
          meals_logged: number;
          meals_completed: boolean;
          sleep_completed: boolean;
          points: number;
          inserted_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          date?: string;
          water_ml?: number;
          workout_completed?: boolean;
          sleep_hours?: number | null;
          meals_logged?: number;
          meals_completed?: boolean;
          sleep_completed?: boolean;
          points?: number;
          inserted_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          date?: string;
          water_ml?: number;
          workout_completed?: boolean;
          sleep_hours?: number | null;
          meals_logged?: number;
          meals_completed?: boolean;
          sleep_completed?: boolean;
          points?: number;
          inserted_at?: string;
          updated_at?: string;
        };
      };
      desafios_disponiveis: {
        Row: {
          id: string;
          titulo: string;
          descricao: string;
          regra: string;
          icone: string;
          objetivo: string;
          created_at: string;
        };
        Insert: {
          id?: string;
          titulo: string;
          descricao: string;
          regra: string;
          icone: string;
          objetivo: string;
          created_at?: string;
        };
        Update: {
          id?: string;
          titulo?: string;
          descricao?: string;
          regra?: string;
          icone?: string;
          objetivo?: string;
          created_at?: string;
        };
      };
      desafio_ativo_usuario: {
        Row: {
          user_id: string;
          desafio_id: string;
          data_inicio: string;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          user_id: string;
          desafio_id: string;
          data_inicio?: string;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          user_id?: string;
          desafio_id?: string;
          data_inicio?: string;
          created_at?: string;
          updated_at?: string;
        };
      };
      anotacoes_desafio: {
        Row: {
          id: string;
          user_id: string;
          desafio_id: string;
          texto: string;
          created_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          desafio_id: string;
          texto: string;
          created_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          desafio_id?: string;
          texto?: string;
          created_at?: string;
        };
      };
      user_desafios: {
        Row: {
          id: string;
          user_id: string;
          desafio_id: string;
          status: string;
          progresso: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          desafio_id: string;
          status?: string;
          progresso?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          desafio_id?: string;
          status?: string;
          progresso?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
    };
  };
};
